import type { FetchResult } from '@apollo/client';
import {
  useCreditCheckMutation,
  useCheckCreditCheckStatusLazyQuery,
  type CreditCheckMutation,
  type EncryptedToken,
  type InputMaybe,
  type ServiceabilityTvInternetProductsInput,
  type ServiceabilityPricingInput,
  type ServiceabilityOfferMetadataInput,
  type ServiceabilityOfferMetadata,
  CreditCheckResultEnum,
  type CheckCreditCheckStatusQuery,
  useCheckCreditCheckStatusByCustomerInformationLazyQuery,
  CheckCreditCheckStatusByCustomerInformationQuery,
} from '@updater/consumer-graph';
import { useFeature } from 'context/growthbook/useFeature';
import { useUserData } from 'flows/core/hooks/use-profile';
import { useCallback, useEffect } from 'react';
import { deepOmitTypename } from '@updater/consumer-graph/utils';
import { useBuyFlowContextOptional } from '../generic-buyflow/contexts/BuyFlowContext';

export type CreditCheckStatus = {
  creditCheckResult?: CreditCheckResultEnum;
  ssnPiiToken?: string;
  dobPiiToken?: string;
  hasExecutedCreditCheck: boolean;
};

export interface UseXfinityCreditCheckReturn extends CreditCheckStatus {
  isInXfinityCreditCheckFeature: boolean;
  xfinityDobSsnEnabled: boolean;
  loading: boolean;
  performCreditCheck: ({
    tokenizedDob,
    tokenizedSsn,
  }: {
    tokenizedDob: EncryptedToken;
    tokenizedSsn: EncryptedToken;
  }) => Promise<FetchResult<CreditCheckMutation>>;
  lazyFetchCreditCheckStatus: () => Promise<CreditCheckStatus>;
}

export interface UseXfinityCreditCheckArgs {
  skipCreditCheckQueryOnMount?: boolean;
}

export const useXfinityCreditCheck = (
  { skipCreditCheckQueryOnMount }: UseXfinityCreditCheckArgs = {
    skipCreditCheckQueryOnMount: false,
  }
): UseXfinityCreditCheckReturn => {
  /**
   * Epic - https://updater.atlassian.net/browse/TVI-1868
   * Growthbook - https://growthbook.prod.updatron.tools/features/consumer_app_xfinity_dob_ssn?new
   */
  const { value: xfinityDobSsnEnabled, loading: loadingXfinityDobSsnEnabled } =
    useFeature('consumer_app_xfinity_dob_ssn');

  const { user, loading: userDataLoading } = useUserData();

  const moveId = user?.currentMove?.id ?? '';

  // Use optional context to support components outside of BuyFlowProvider
  const buyFlowContext = useBuyFlowContextOptional();
  const { reservation, customerInfo, isGuestMode, installAddress } =
    buyFlowContext || {};

  const { provider, offer } = reservation || {};

  const [creditCheckMutation] = useCreditCheckMutation();

  const isXfinityOffer = provider?.code?.toLowerCase() === 'xfinity';
  const isInXfinityCreditCheckFeature = isXfinityOffer && xfinityDobSsnEnabled;

  // Use lazy query instead of a regular query.
  const [
    fetchCreditCheckStatusLazyQuery,
    {
      data: creditCheckStatusByMoveIdData,
      loading: loadingCheckCreditCheckByMoveIdStatus,
      called: calledMoveIdQuery,
    },
  ] = useCheckCreditCheckStatusLazyQuery();

  // Use lazy query instead of a regular query.
  const [
    fetchCreditCheckStatusByCustomerInfoLazyQuery,
    {
      data: creditCheckStatusByCustomerInfoData,
      loading: loadingCheckCreditCheckByCustomerInfoStatus,
      called: calledCustomerInfoQuery,
    },
  ] = useCheckCreditCheckStatusByCustomerInformationLazyQuery();

  const hasRequiredCustomerInfo =
    Boolean(customerInfo?.firstName?.trim()) &&
    Boolean(customerInfo?.lastName?.trim()) &&
    Boolean(customerInfo?.email?.trim()) &&
    Boolean(customerInfo?.phone?.trim());

  // Trigger the lazy query if the conditions are met.
  useEffect(() => {
    if (!skipCreditCheckQueryOnMount && isInXfinityCreditCheckFeature) {
      if (!isGuestMode && moveId && !calledMoveIdQuery) {
        fetchCreditCheckStatusLazyQuery({
          variables: { moveId },
        });
      } else if (
        isGuestMode &&
        hasRequiredCustomerInfo &&
        !calledCustomerInfoQuery
      ) {
        fetchCreditCheckStatusByCustomerInfoLazyQuery({
          variables: {
            customerInformation: {
              firstName: customerInfo?.firstName || '',
              lastName: customerInfo?.lastName || '',
              email: customerInfo?.email || '',
              phone: customerInfo?.phone || '',
            },
          },
          fetchPolicy: 'network-only',
        });
      }
    }
  }, [
    isInXfinityCreditCheckFeature,
    calledMoveIdQuery,
    calledCustomerInfoQuery,
    moveId,
    fetchCreditCheckStatusLazyQuery,
    customerInfo,
    fetchCreditCheckStatusByCustomerInfoLazyQuery,
    skipCreditCheckQueryOnMount,
    isGuestMode,
  ]);

  const performCreditCheck = useCallback(
    ({
      tokenizedDob,
      tokenizedSsn,
    }: {
      tokenizedDob: EncryptedToken;
      tokenizedSsn: EncryptedToken;
    }) => {
      return creditCheckMutation({
        variables: {
          input: {
            offer: {
              code: offer?.code,
              metadata: serviceabilityOfferMetadataInputAdapter({
                metadata:
                  offer?.metadata ?? ({} as ServiceabilityOfferMetadata),
              }),
              name: offer?.name,
              pricing: deepOmitTypename(
                offer?.pricing
              ) as InputMaybe<ServiceabilityPricingInput>,
              products: deepOmitTypename(
                offer?.products
              ) as InputMaybe<ServiceabilityTvInternetProductsInput>,
              providerCode: provider?.code,
              providerOfferId: offer?.providerOfferId,
            },
            tokenizedDob,
            tokenizedSsn,
            moveId: moveId || undefined,
            // Address is required when moveId is not provided (guest mode)
            address: !moveId
              ? {
                  street: installAddress?.street || '',
                  city: installAddress?.city || '',
                  state: installAddress?.state || '',
                  postalCode: installAddress?.postalCode || '',
                  unit: installAddress?.unit || undefined,
                }
              : undefined,
            customerInformation: {
              firstName: customerInfo?.firstName ?? 'not provided',
              lastName: customerInfo?.lastName ?? 'not provided',
              email: customerInfo?.email ?? 'not provided',
              phone: customerInfo?.phone ?? '',
            },
          },
        },
        refetchQueries: ['CheckCreditCheckStatus'],
      });
    },
    [creditCheckMutation, moveId, offer, provider, customerInfo, installAddress]
  );

  const lazyFetchCreditCheckStatus = useCallback(async () => {
    // Auth mode: use moveId, Guest mode: use customerInfo
    const canFetchByMoveId = !isGuestMode && moveId;
    const canFetchByCustomerInfo = isGuestMode && hasRequiredCustomerInfo;

    if (!canFetchByMoveId && !canFetchByCustomerInfo) {
      return {
        creditCheckResult: undefined,
        ssnPiiToken: undefined,
        dobPiiToken: undefined,
        hasExecutedCreditCheck: false,
      };
    }

    let creditCheckData: CheckCreditCheckStatusQuery | undefined;

    if (canFetchByMoveId) {
      const { data } = await fetchCreditCheckStatusLazyQuery({
        variables: { moveId },
      });
      creditCheckData = data;
    } else {
      const { data } = await fetchCreditCheckStatusByCustomerInfoLazyQuery({
        variables: {
          customerInformation: {
            firstName: customerInfo?.firstName || '',
            lastName: customerInfo?.lastName || '',
            email: customerInfo?.email || '',
            phone: customerInfo?.phone || '',
          },
        },
        fetchPolicy: 'network-only',
      });
      creditCheckData = data;
    }

    const {
      creditCheckResult,
      hasExecutedCreditCheck,
      ssnPiiToken,
      dobPiiToken,
    } = extractCreditCheckData(creditCheckData);

    return {
      creditCheckResult,
      ssnPiiToken,
      dobPiiToken,
      hasExecutedCreditCheck,
    };
  }, [
    fetchCreditCheckStatusLazyQuery,
    fetchCreditCheckStatusByCustomerInfoLazyQuery,
    hasRequiredCustomerInfo,
    moveId,
    isGuestMode,
    customerInfo,
  ]);

  let creditCheckStatusData =
    creditCheckStatusByMoveIdData ?? creditCheckStatusByCustomerInfoData;

  const {
    creditCheckResult,
    checkCreditCheckStatus,
    hasExecutedCreditCheck,
    ssnPiiToken,
    dobPiiToken,
  } = extractCreditCheckData(creditCheckStatusData);

  // When loading === false the result is not immediately ready until the next render
  const isCreditCheckStatusResultReady = () => {
    if (!moveId && !hasRequiredCustomerInfo) {
      // If we don't have the required info to fetch credit check status, don't wait for it.
      return true;
    }

    return (
      !isInXfinityCreditCheckFeature ||
      creditCheckResult !== undefined ||
      checkCreditCheckStatus === null
    );
  };

  const loading =
    loadingXfinityDobSsnEnabled ||
    userDataLoading ||
    loadingCheckCreditCheckByCustomerInfoStatus ||
    loadingCheckCreditCheckByMoveIdStatus ||
    !isCreditCheckStatusResultReady();

  return {
    xfinityDobSsnEnabled,
    isInXfinityCreditCheckFeature,
    hasExecutedCreditCheck,
    creditCheckResult,
    ssnPiiToken,
    dobPiiToken,
    loading,
    lazyFetchCreditCheckStatus,
    performCreditCheck,
  };
};

export const serviceabilityOfferMetadataInputAdapter = ({
  metadata,
}: {
  metadata: ServiceabilityOfferMetadata;
}): ServiceabilityOfferMetadataInput => {
  return {
    bulkAgreement: metadata.bulkAgreement,
    cachedAt: metadata.cachedAt,
    dummy: metadata.dummy,
    dummyOfferType: metadata.dummyOfferType,
    fromCache: metadata.fromCache,
    misc: metadata.misc,
  };
};

type CreditCheckStatusData =
  | CheckCreditCheckStatusQuery
  | CheckCreditCheckStatusByCustomerInformationQuery;

const getCheckStatus = (data?: CreditCheckStatusData) => {
  if (!data) return undefined;

  if ('checkCreditCheckStatus' in data) {
    return data.checkCreditCheckStatus;
  }

  if ('checkCreditCheckStatusByCustomerInfo' in data) {
    return data.checkCreditCheckStatusByCustomerInfo;
  }

  return undefined;
};

export const extractCreditCheckData = (data?: CreditCheckStatusData) => {
  const checkStatus = getCheckStatus(data);
  const creditCheckResult = checkStatus?.creditCheckOutput?.result;

  return {
    creditCheckResult,
    checkCreditCheckStatus: checkStatus,
    hasExecutedCreditCheck: creditCheckResult != null,
    ssnPiiToken: checkStatus?.tokenizedSsn?.token,
    dobPiiToken: checkStatus?.tokenizedDob?.token,
  };
};
